import { useState, useEffect, useCallback } from 'react';
import signalRService from '../utils/signalR';
import { DevicesRepository } from '../repositories/devices';
import { toast } from 'sonner';
import {useParams} from "react-router-dom";

const useDeviceEvents = (deviceId) => {
  const [events, setEvents] = useState([]);
  const { building: buildingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  // Load initial events
  const loadInitialEvents = useCallback(async () => {
    if (!deviceId) return;
    
    try {
      setLoading(true);
      const response = await DevicesRepository.getDeviceEvents(deviceId);
      
      if (!response.success) {
        setError(response.error || response.message);
        toast.error('Failed to load device events');
        return;
      }
      console.log('Initial events loaded:', response.data);
      setEvents(response.data || []);
    } catch (err) {
      setError(err.message);
      toast.error('Error loading device events');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  // Handle new event from SignalR
  const handleNewEvent = useCallback((data) => {
    console.log('Received new event from SignalR:', data);
    if (data && data.deviceId === parseInt(deviceId)) {
      setEvents(prevEvents => {
        // Create a new event object with the received data
        const newEvent = {
          id: Date.now(), // Generate a unique ID
          deviceId: data.deviceId,
          description: data.description,
          date: data.date || new Date().toISOString(),
          code: data.code || '',
          status: data.status || 20, // Default to "Running" if not specified
        };
        
        // Add to the beginning of the events array
        return [newEvent, ...prevEvents];
      });
    }
  }, [deviceId]);

  // Setup SignalR connection
  useEffect(() => {
    if (!deviceId) return;

    const setupSignalR = async (retryCount = 0) => {
      try {
        // Start the connection
        const started = await signalRService.start();
        if (!started) {
          throw new Error('Failed to start SignalR connection');
        }

        // Subscribe to the device group
        const subscribed = await signalRService.subscribeToGroup(buildingId);
        if (!subscribed) {
          throw new Error('Failed to subscribe to device group');
        }

        // Register event handler



        signalRService.on('NotifyDeviceForLocation', (data) => {

          setEvents(prevEvents => {

            const deviceIndex = prevEvents.findIndex(dev => dev.id === data.deviceId);

            if (deviceIndex !== -1) {

              const updatedDevices = [...prevEvents];

              updatedDevices[deviceIndex].location = data.location;

              return updatedDevices;

            }

            return [...prevEvents, { id: data.deviceId, location: data.location }];

          });

        });



        signalRService.on('NotifyDeviceForDoor', (data) => {

          setEvents(prevEvents => {

            const deviceIndex = prevEvents.findIndex(dev => dev.id === data.deviceId);

            if (deviceIndex !== -1) {

              const updatedDevices = [...prevEvents];

              updatedDevices[deviceIndex].door = (data.door===10? 'Open' : 'Close');

              updatedDevices[deviceIndex].status = (data.status===10? 'Grey' : (data.status===20? 'Green' : (data.status===30? 'Blue' : (data.status===40? 'Yellow' : 'Red'))));

              return updatedDevices;

            }

            return [...prevEvents, { id: data.deviceId, status: (data.status===10? 'Grey' : (data.status===20? 'Green' : (data.status===30? 'Blue' : (data.status===40? 'Yellow' : 'Red'))))}];

          });

        });

        signalRService.on('NotifyDeviceForDirection', (data) => {

          setEvents(prevEvents => {

            const deviceIndex = prevEvents.findIndex(dev => dev.id === data.deviceId);

            if (deviceIndex !== -1) {

              const updatedDevices = [...prevEvents];

              updatedDevices[deviceIndex].direction = (data.direction===10? 'Stationary' : (data.direction===20 ? 'Up' : 'Down'));

              return updatedDevices;

            }

            return [...prevEvents, { id: data.deviceId, direction: (data.direction===10? 'Stationary' : (data.direction===20 ? 'Up' : 'Down'))}];

          });

        });



        signalRService.on('NotifyDeviceForTemperature', (data) => {

          setEvents(prevEvents => {

            const deviceIndex = prevEvents.findIndex(dev => dev.id === data.deviceId);

            if (deviceIndex !== -1) {

              const updatedDevices = [...prevEvents];

              updatedDevices[deviceIndex].temperature = data.temperature;

              return updatedDevices;

            }

            return [...prevEvents, { id: data.deviceId, temperature: data.temperature}];

          });

        });



        signalRService.on("NotifyDeviceForEvent", (data) => {
          console.log('New event received:', data);
          let li = document.createElement("li");

          document.getElementById("messagesList1").appendChild(li);

          li.textContent = data.deviceId + ' - ' + data.date + ' - ' + data.description;

        });

        // Cleanup the connection when the component unmounts

        setConnected(true);
        return () => {

          signalRService.stop().catch(err => console.error('Error stopping SignalR connection:', err));

        };

      } catch (err) {
        setConnected(false);
        if (retryCount > 5) {
          setError(err.message);
          toast.error('Failed to connect to real-time updates');
        } else {
          console.error(`SignalR connection error (attempt ${retryCount + 1}):`, err);
          setTimeout(() => setupSignalR(retryCount + 1), 5000); // Retry after 5 seconds
        }
      }
    };

    // Load initial events and setup SignalR
    loadInitialEvents();
    setupSignalR();

    return () => {
      // Cleanup will be handled by the setupSignalR function
    };
  }, [deviceId, handleNewEvent, loadInitialEvents]);

  return { events, loading, error, connected };
};

export default useDeviceEvents;
